import type { ComponentType, Context, PropsWithChildren } from 'react'
import { createContext, useContext } from 'react'

interface ServiceList {
  [key: string | symbol]: unknown
}

type HookName<Property> = `use${Capitalize<string & Property>}`

type FetchContextHooks<T extends ServiceList> = {
  [Property in keyof T as HookName<Property>]: () => T[Property]
}

type ProviderName<
  DisplayName extends string,
  Prefix extends string,
> = `${Prefix}${Capitalize<DisplayName>}Provider`

type DefaultProviderName<D extends string> = ProviderName<D, 'Default'>
type DefaultProviderType = ComponentType<PropsWithChildren>

type CustomProviderName<D extends string> = ProviderName<D, 'Custom'>
type CustomProviderType<T> = ComponentType<PropsWithChildren<{ value: T }>>

type ProviderNameType<D extends string> =
  | DefaultProviderName<D>
  | CustomProviderName<D>

type AutoFetchContext<
  T extends ServiceList,
  D extends string,
> = FetchContextHooks<T> & {
  [key in ProviderNameType<D>]: key extends CustomProviderName<D>
    ? CustomProviderType<T>
    : DefaultProviderType
}

function capitalize(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1)
}

function formatProviderName<DisplayName extends string, Prefix extends string>(
  displayName: DisplayName,
  prefix: Prefix,
) {
  const name = `${prefix}${capitalize(displayName)}Provider`

  return name as ProviderName<DisplayName, Prefix>
}

export function createFetchContext<
  T extends ServiceList,
  DisplayName extends string,
>(displayName: DisplayName, services: T): AutoFetchContext<T, DisplayName> {
  const type = createContext<T | undefined>(undefined)

  type.displayName = displayName

  const hooks = createHooks(displayName, services, type)
  const defaultProvider = createDefaultProvider(displayName, services, type)
  const defaultProviderName = formatProviderName(displayName, 'Default')
  const customProvider = createCustomProvider(displayName, type)
  const customProviderName = formatProviderName(displayName, 'Custom')

  // We need to cast to unknown and then back to the correct type to go around
  // typescript's validation here because of the way we are remapping the keys.
  return {
    ...hooks,
    [defaultProviderName]: defaultProvider,
    [customProviderName]: customProvider,
  } as unknown as AutoFetchContext<T, DisplayName>
}

function createHooks<T extends ServiceList>(
  displayName: string,
  services: T,
  contextType: Context<T | undefined>,
): FetchContextHooks<T> {
  function useHook() {
    const context = useContext(contextType)

    if (!context) {
      throw new Error(
        `use${displayName} must be used within a ${displayName}Provider`,
      )
    }

    return context
  }

  useHook.displayName = `use${displayName}`

  return Object.entries(services).reduce((hooks, [fieldName]) => {
    const capitalizedFieldName = capitalize(fieldName)

    function useField() {
      const context = useHook()

      return context[fieldName]
    }

    useField.displayName = `use${capitalizedFieldName}`

    return {
      ...hooks,
      [`use${capitalizedFieldName}`]: useField,
    }
  }, {} as FetchContextHooks<T>)
}

function createDefaultProvider<T extends ServiceList>(
  displayName: string,
  services: T,
  contextType: Context<T | undefined>,
): DefaultProviderType {
  const Provider = ({ children }: PropsWithChildren) => {
    return (
      <contextType.Provider value={services}>{children}</contextType.Provider>
    )
  }

  Provider.displayName = `Default${displayName}Provider`

  return Provider
}

export function createCustomProvider<T extends ServiceList>(
  displayName: string,
  contextType: Context<T | undefined>,
): CustomProviderType<T> {
  const Provider = ({ children, value }: PropsWithChildren & { value: T }) => {
    return <contextType.Provider value={value}>{children}</contextType.Provider>
  }

  Provider.displayName = `Custom${displayName}Provider`

  return Provider
}
